import React from 'react';
import styled from 'styled-components/macro';
import { Formik, Field as FormikField, Form, FormikHelpers } from 'formik';
import { Typography } from '@deeptrue-frontend/ui';
import * as Yup from 'yup';
import {
  Button,
  Textarea as TextareaUi,
  showToast,
} from '@deeptrue-frontend/ui';
import Papa from 'papaparse';
import api from 'api';
import { useFormatMessage } from 'components/IntlProvider';
import { FormatMessage } from 'components/IntlProvider/hooks/hooks';

const BtnWrapper = styled.div`
  display: flex;
  justify-content: flex-start;
  .btn-primary {
    font-size: 1rem;
  }
`;

const StyledTitle = styled(Typography)`
  font-weight: 500;
  font-size: 1.125rem;
`;

const Textarea = styled(TextareaUi)`
  width: 100%;
  min-height: 10rem;
  font-size: 1rem;
  padding: 1.125rem 0.875rem;
`;

const FormWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 40rem;
`;

const getValidationSchema = (t: FormatMessage) =>
  Yup.object().shape<FormValues>({
    emails: Yup.string().required(
      t('errors.fields.required', 'Field is required')
    ),
  });

export interface FormValues {
  emails: string;
}

interface Props {
  organizationCodename: string;
  uuid: string;
  setRespondentsAdded: (val: boolean) => void;
}

const UploadPanelistEmails = ({
  uuid,
  organizationCodename,
  setRespondentsAdded,
}: Props) => {
  const t = useFormatMessage();

  const submit = (
    { emails }: { emails: string },
    { setErrors, setFieldValue }: FormikHelpers<FormValues>
  ) => {
    const separatedEmails = emails.split(',');
    const correctEmails: string[][] = [];

    separatedEmails.forEach((email: string) => {
      correctEmails.push([email.trim()]);
    });

    const csv = Papa.unparse({
      fields: ['email'],
      data: correctEmails,
    });

    const blob = new Blob([csv], { type: 'text/csv' });

    return api.resources
      .uploadRespondantsList({
        uuid,
        codename: organizationCodename,
        file: blob,
      })
      .then(({ data }: any) => {
        const emailWithErrors = data.bad_rows.map(
          (errEmail: any) => errEmail.field_value
        );

        if (emailWithErrors.length > 0) {
          setErrors({
            emails: t(
              'collectData.email.invalid',
              '{testedEmail} have invalid email format',
              { testedEmail: emailWithErrors.toString() }
            ),
          });
          setFieldValue('emails', emailWithErrors.toString(), false);
        }

        setRespondentsAdded(true);
        if (data.correct_rows > 0) {
          showToast({
            type: 'success',
            title: t(
              'project.collectData.manual.emails.success',
              'Succesfully uploaded emails'
            ),
          });
        }
      })
      .catch(() => {
        showToast({
          type: 'error',
          title: t(
            'errors.toast.general',
            'Something went wrong, please contact administrator'
          ),
        });
      });
  };

  return (
    <FormWrapper>
      <Formik
        initialValues={{ emails: '' }}
        onSubmit={submit}
        validationSchema={getValidationSchema(t)}
      >
        {({ handleSubmit, errors, touched, isSubmitting }) => {
          return (
            <Form onSubmit={handleSubmit}>
              <StyledTitle variant="h5">
                {t('project.collectData.manual.emails', 'Email Contacts')}
              </StyledTitle>
              <Typography
                color="#5C6370"
                fontSize="0.875rem"
                weight="regular"
                mb="0.5rem"
              >
                {t(
                  'project.colletData.emails.input',
                  'Enter the emails separated with comma.'
                )}
              </Typography>
              <FormikField
                name="emails"
                as={Textarea}
                hasError={touched.emails && errors.emails}
                helperText={touched.emails && errors.emails}
                placeholder={t(
                  'project.collectData.manual.textarea.placeholder',
                  'Enter here the emails of recipients'
                )}
              />

              <BtnWrapper>
                <Button
                  isLoading={isSubmitting}
                  type="submit"
                  variant="primary"
                  color="primary"
                  data-testid="add_emails-button"
                >
                  {t('collectData.uploadEmails', 'Upload Emails')}
                </Button>
              </BtnWrapper>
            </Form>
          );
        }}
      </Formik>
    </FormWrapper>
  );
};

export default UploadPanelistEmails;
